/**
 * The one WebSocket the SPA keeps open (D88: a change reaches the screen within five
 * seconds, and polling is the fallback when the socket is not there).
 *
 * The socket carries identifiers only (see `core/realtime.py`), so this module never
 * puts business data into a cache. It turns a frame into "these query keys are stale"
 * and TanStack Query refetches through the REST API, which is where scoping lives.
 *
 * The pure parts (URL, backoff, frame parsing, key mapping) are exported and unit-tested.
 * The class takes its socket, timers and token source as parameters, so the reconnect
 * and fallback behaviour is testable without a browser.
 */
import { queryKeys } from "@/api/queryKeys";

/** Sub-protocol the server accepts. Must equal `core.realtime.WS_PROTOCOL`. */
export const WS_PROTOCOL = "kapitan.v1";

/** Path of the one socket endpoint. Must equal `core/routing.py`. */
export const WS_PATH = "/ws/inbox/";

/** How often the fallback refetches while the socket is down (the TZ's polling number). */
export const POLL_INTERVAL_MS = 15_000;

/** Close code the server uses for a refused handshake (`core.consumers.CLOSE_UNAUTHORIZED`). */
export const CLOSE_UNAUTHORIZED = 4401;

const MAX_BACKOFF_MS = 30_000;

export const FRAME_TYPES = [
  "message.new",
  "conversation.updated",
  "conversation.typing",
  "sla.changed",
  "approval.queued",
  "notification.new",
  "search.progress",
  "deal.updated",
] as const;

export type FrameType = (typeof FRAME_TYPES)[number];

export interface RealtimeFrame {
  type: FrameType;
  conversation_id?: string;
  /** Set on `conversation.typing`: the person writing in the thread. */
  user_id?: string;
  notification_id?: string;
  /** A tour search whose part for one supplier has finished (D168). */
  search_id?: string;
}

export type QueryKeyPrefix = readonly unknown[];

/** `https://api.example` becomes `wss://api.example/ws/inbox/`. Pure, so it is testable. */
export function realtimeUrl(apiBase: string): string {
  const url = new URL(WS_PATH, apiBase.endsWith("/") ? apiBase : `${apiBase}/`);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

/** 1 s, 2 s, 4 s ... capped at 30 s. A server restart must not be met by a stampede. */
export function reconnectDelayMs(attempt: number): number {
  const exponent = Math.min(Math.max(attempt, 0), 10);
  return Math.min(MAX_BACKOFF_MS, 1_000 * 2 ** exponent);
}

/**
 * A frame from the wire, or `null` when it is not one we know. Unknown types are dropped
 * rather than thrown: a newer server may send a frame this build predates, and that must
 * not break the socket for the frames it does understand.
 */
export function parseFrame(raw: unknown): RealtimeFrame | null {
  if (typeof raw !== "string") {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (parsed === null || typeof parsed !== "object") {
    return null;
  }
  const candidate = parsed as Record<string, unknown>;
  const type = candidate.type;
  if (typeof type !== "string" || !(FRAME_TYPES as readonly string[]).includes(type)) {
    return null;
  }
  const frame: RealtimeFrame = { type: type as FrameType };
  if (typeof candidate.conversation_id === "string") {
    frame.conversation_id = candidate.conversation_id;
  }
  if (typeof candidate.notification_id === "string") {
    frame.notification_id = candidate.notification_id;
  }
  if (typeof candidate.search_id === "string") {
    frame.search_id = candidate.search_id;
  }
  return frame;
}

/**
 * One conversation's cache key, or every conversation's when the frame names none. The wire
 * carries the id as a string and the cache as a number, and TanStack compares key parts
 * strictly, so the id has to go through the same factory the screen caches with.
 */
function oneConversation(
  key: (id: number) => QueryKeyPrefix,
  frame: RealtimeFrame,
): QueryKeyPrefix {
  return frame.conversation_id === undefined
    ? key(0).slice(0, 2)
    : key(Number(frame.conversation_id));
}

/**
 * Which cached queries a frame makes stale. The keys are prefixes, matched the way
 * TanStack matches them, so `["inbox", "thread"]` reaches every open thread.
 */
export function keysForFrame(frame: RealtimeFrame): QueryKeyPrefix[] {
  switch (frame.type) {
    case "message.new":
    case "conversation.updated":
    case "sla.changed":
      return [
        ["inbox", "list"],
        oneConversation(queryKeys.inbox.conversation, frame),
        oneConversation(queryKeys.inbox.thread, frame),
        // The chips, the free queue and the open thread's panel move with the same frames (09 §7).
        ["inbox", "counts"],
        ["inbox", "queue"],
        oneConversation(queryKeys.inbox.panel, frame),
        // The sidebar's Murojaatlar and Suhbatlar numbers move with the same frames.
        ["nav", "counts"],
      ];
    case "approval.queued":
      // A waiting draft moves the "AI javobi tasdiqda" chip and the row's status word.
      return [
        ["ai-drafts"],
        oneConversation(queryKeys.inbox.thread, frame),
        ["inbox", "counts"],
        ["inbox", "list"],
      ];
    case "notification.new":
      return [["notifications"]];
    case "search.progress":
      // The page refetches through REST, which applies the scoping (D168). The key is the
      // whole family, because the cached key holds the session id as a number and a frame
      // carries it as a string, so a per-id prefix would never match.
      return [["tour-search"]];
    case "deal.updated":
      // A refetch-only frame (D650): the lists and the open deal come back through the scoped REST.
      return [queryKeys.kanban(), queryKeys.deals.all(), queryKeys.deal.all()];
    case "conversation.typing":
      // A typing ping changes nothing on the server's side, so no query is stale. The
      // presence store takes the frame directly (see `useRealtimeSync`).
      return [];
  }
}

/** Every inbox key. Used by the fallback tick, which cannot know what changed. */
export const ALL_INBOX_KEYS: readonly QueryKeyPrefix[] = [
  ["inbox"],
  ["ai-drafts"],
  ["notifications"],
  ["tour-search"],
  queryKeys.kanban(),
  queryKeys.deals.all(),
];

export type RealtimeStatus = "idle" | "connecting" | "open" | "retrying" | "closed";

/**
 * The slice of the WebSocket API the client uses. The handler parameter types are the
 * DOM's own, so a real `WebSocket` satisfies this without a cast; a test passes a fake.
 */
export interface SocketLike {
  onopen: ((event: Event) => void) | null;
  onmessage: ((event: MessageEvent) => void) | null;
  onclose: ((event: CloseEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  close(code?: number, reason?: string): void;
}

export type SocketFactory = (url: string, protocols: readonly string[]) => SocketLike;

export interface TimerLike {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  setInterval(callback: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
}

export interface RealtimeOptions {
  url: string;
  createSocket: SocketFactory;
  /** The current access token, read again on every connect so a refresh is picked up. */
  readToken: () => string | null;
  /** Exchange the refresh cookie for a new token. `null` when the session is gone. */
  refreshToken: () => Promise<string | null>;
  onFrame: (frame: RealtimeFrame) => void;
  /** Called every POLL_INTERVAL_MS while the socket is not open. */
  onPollTick: () => void;
  onStatus?: (status: RealtimeStatus) => void;
  timers?: TimerLike;
}

const BROWSER_TIMERS: TimerLike = {
  setTimeout: (callback, ms): unknown => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle): void => {
    globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>);
  },
  setInterval: (callback, ms): unknown => globalThis.setInterval(callback, ms),
  clearInterval: (handle): void => {
    globalThis.clearInterval(handle as ReturnType<typeof setInterval>);
  },
};

/**
 * Keeps one socket open, reconnects with backoff, and polls while it is down.
 *
 * A 4401 close means the token was refused: the client refreshes it once and tries
 * again. If the refresh fails the client stops and says so; the session guard sends
 * the person to sign-in, which is the only place a dead session belongs.
 */
export class RealtimeClient {
  private readonly options: RealtimeOptions;
  private readonly timers: TimerLike;
  private socket: SocketLike | null = null;
  private status: RealtimeStatus = "idle";
  private attempt = 0;
  private reconnectHandle: unknown = null;
  private pollHandle: unknown = null;
  private stopped = true;
  private refreshedForClose = false;

  constructor(options: RealtimeOptions) {
    this.options = options;
    this.timers = options.timers ?? BROWSER_TIMERS;
  }

  start(): void {
    if (!this.stopped) {
      return;
    }
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearReconnect();
    this.stopPolling();
    if (this.socket !== null) {
      const socket = this.socket;
      this.socket = null;
      socket.close(1000, "closed by client");
    }
    this.setStatus("closed");
  }

  /** The status the UI can show ("Jonli" vs "Qayta ulanmoqda"). */
  getStatus(): RealtimeStatus {
    return this.status;
  }

  private connect(knownToken?: string): void {
    this.setStatus("connecting");
    // A token handed over by a refresh is used as it is: reading storage again could
    // return null on a browser that refuses it, and the refresh would loop.
    const token = knownToken ?? this.options.readToken();
    if (token === null) {
      void this.refreshAndConnect();
      return;
    }
    let socket: SocketLike;
    try {
      socket = this.options.createSocket(this.options.url, [WS_PROTOCOL, token]);
    } catch {
      // A browser may refuse the socket before it exists: Firefox throws
      // NS_ERROR_CONTENT_BLOCKED when the page's CSP does not name the ws origin. That is a
      // closed connection, not a crash: back off and keep the inbox fresh by polling.
      this.socket = null;
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = (): void => {
      this.attempt = 0;
      this.refreshedForClose = false;
      this.stopPolling();
      this.setStatus("open");
    };
    socket.onmessage = (event: MessageEvent): void => {
      const frame = parseFrame(event.data);
      if (frame !== null) {
        this.options.onFrame(frame);
      }
    };
    socket.onclose = (event: CloseEvent): void => {
      this.socket = null;
      if (this.stopped) {
        return;
      }
      if (event.code === CLOSE_UNAUTHORIZED && !this.refreshedForClose) {
        this.refreshedForClose = true;
        void this.refreshAndConnect();
        return;
      }
      this.scheduleReconnect();
    };
    socket.onerror = (): void => {
      // The close event that follows carries the reason; nothing to do here.
    };
  }

  private async refreshAndConnect(): Promise<void> {
    const fresh = await this.options.refreshToken();
    if (this.stopped) {
      return;
    }
    if (fresh === null) {
      this.stop();
      return;
    }
    this.connect(fresh);
  }

  private scheduleReconnect(): void {
    this.setStatus("retrying");
    this.startPolling();
    const delay = reconnectDelayMs(this.attempt);
    this.attempt += 1;
    this.clearReconnect();
    this.reconnectHandle = this.timers.setTimeout(() => {
      this.reconnectHandle = null;
      this.connect();
    }, delay);
  }

  private startPolling(): void {
    if (this.pollHandle !== null) {
      return;
    }
    this.pollHandle = this.timers.setInterval(() => {
      this.options.onPollTick();
    }, POLL_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollHandle !== null) {
      this.timers.clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  private clearReconnect(): void {
    if (this.reconnectHandle !== null) {
      this.timers.clearTimeout(this.reconnectHandle);
      this.reconnectHandle = null;
    }
  }

  private setStatus(next: RealtimeStatus): void {
    this.status = next;
    this.options.onStatus?.(next);
  }
}

/** Calls `run` once, `waitMs` after the last call: a burst of frames is one refetch. */
export function trailing(run: () => void, waitMs: number): { call(): void; cancel(): void } {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const cancel = (): void => {
    clearTimeout(timer);
  };
  return {
    call: (): void => {
      cancel();
      timer = setTimeout(run, waitMs);
    },
    cancel,
  };
}
